import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { IWorkoutStatisticsViewModel } from "../models/workoutStatisticsViewModel";

export interface IStatisticsRangeForm {
  dateFrom: Date | null;
  dateTo: Date | null;
}

/** Local-date → `YYYY-MM-DD` (no timezone shift). Same approach as GymPage. */
const toApiDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

/** Midnight today and seven days back — the default "last week" window. */
const lastWeekRange = (): IStatisticsRangeForm => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 7);
  return { dateFrom: from, dateTo: to };
};

export interface IUseWorkoutStatisticsArgs {
  /**
   * Statistics endpoint to hit. Defaults to the caller-scoped
   * `GetMyWorkoutStatistics`; the admin user-profile view passes
   * `GetUserWorkoutStatistics` together with a `userId` in `extraParams`.
   */
  endpoint?: string;
  /** Query-key base (kept separate so distinct data sources don't share a cache). */
  queryKeyBase?: string;
  /** Extra query-string params merged before `dateFrom`/`dateTo` (e.g. `{ userId }`). */
  extraParams?: Record<string, string | number>;
}

/**
 * Owns the statistics tab's date-range form (defaulted to the last week) and the
 * API query for the selected window. React Query refetches whenever the
 * formatted from/to change, and the request stays disabled until both are set.
 */
export const useWorkoutStatistics = ({
  endpoint = endpointsMetadata.workoutPlan.getMyWorkoutStatistics,
  queryKeyBase = endpointsMetadata.workoutPlan.getMyWorkoutStatistics,
  extraParams,
}: IUseWorkoutStatisticsArgs = {}) => {
  const rangeForm = useForm<IStatisticsRangeForm>({
    defaultValues: lastWeekRange(),
  });

  const [range, setRange] = useState<IStatisticsRangeForm>(lastWeekRange);

  useEffect(() => {
    const subscription = rangeForm.watch((values) => {
      if (values.dateFrom && values.dateTo) {
        setRange({ dateFrom: values.dateFrom, dateTo: values.dateTo });
      }
    });
    return () => subscription.unsubscribe();
  }, [rangeForm]);

  const apiFrom = range.dateFrom ? toApiDate(range.dateFrom) : undefined;
  const apiTo = range.dateTo ? toApiDate(range.dateTo) : undefined;
  const enabled = !!apiFrom && !!apiTo;

  const query = new URLSearchParams();
  Object.entries(extraParams ?? {}).forEach(([key, value]) =>
    query.set(key, String(value)),
  );
  if (apiFrom) query.set("dateFrom", apiFrom);
  if (apiTo) query.set("dateTo", apiTo);
  const requestUrl = `${endpoint}?${query.toString()}`;

  const { data, isLoading, isFetching } = useApiQuery<IWorkoutStatisticsViewModel>(
    {
      queryKey: [queryKeyBase, extraParams ?? {}, apiFrom, apiTo],
      endpoint: requestUrl,
      enabled,
      globalIsLoading: false,
    },
  );

  const hasData = useMemo(
    () => !!data && data.summary.workoutCount > 0,
    [data],
  );

  return {
    rangeForm,
    statistics: data,
    hasData,
    isLoading: isLoading || isFetching,
  };
};
